import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';

import {
  getActEventExpertBlockId,
  getActEventExpertsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormExpert } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormExpert';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import { getTitleFieldWithRedStar } from '../../../../functions/getTitleFieldWithRedStar';
import { IKnmActExpertSelectOption } from '../../../../CorePackage/CatalogsModule/interfaces/IKnmActExpertSelectOption';
import styles from './ErknmActItemEventExperts.module.scss';
import { SelectInputField } from '../../../../UIPackage/MainModule/components/SelectInputField';
import { IDecisionFormRef } from '../../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';

interface IErknmActItemEventExpertsProps {
  actEventId: string;
  expertsErknm: IKnmErknmFormExpert[] | undefined;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  involvedExperts: IKnmActExpertSelectOption[] | undefined;
  isWatchOnly: boolean;
  name: string;
}

const ErknmActItemEventExpertsWithoutStore = ({
  actEventId,
  expertsErknm,
  formRef,
  involvedExperts,
  isWatchOnly,
  name,
}: IErknmActItemEventExpertsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  /**
   * Фильтрованный список вариантов для выбора эксперта, без ранее использованных
   */
  const expertsErknmOptionsFiltered = useMemo(() => {
    return (
      expertsErknm
        ?.filter(expert =>
          Array.isArray(involvedExperts) ? !involvedExperts?.find(i => i?.value?.listId === expert.listId) : true,
        )
        .map(expert => {
          return {
            id: expert.guid || '',
            value: expert,
            label: `${expert.title} - ${expert.type?.label}`,
          } as IKnmActExpertSelectOption;
        }) || []
    );
  }, [expertsErknm, JSON.stringify(involvedExperts)]);

  const addExpert = useCallback(() => {
    if (!isWatchOnly && push) {
      push(name, undefined);
    }
  }, [push, isWatchOnly, name]);

  const getExpertsArrayErrorMessage = useCallback((value?: IKnmActExpertSelectOption[], metaError?: unknown) => {
    if (typeof metaError === 'string') {
      return metaError;
    }

    if (Array.isArray(metaError)) {
      return ((metaError as unknown) as Record<string, string | undefined>)['FINAL_FORM/array-error'];
    }

    return undefined;
  }, []);

  const validateExpertsArray = useCallback(
    (value?: IKnmActExpertSelectOption[]) => getExpertsArrayErrorMessage(value),
    [getExpertsArrayErrorMessage],
  );

  const makeRemoveExpertHandler = (index: number) => () => !isWatchOnly && remove && remove(name, index);

  return (
    <FieldBlock
      label="Эксперты/экспертные организации участвовавшие в осуществленном действии"
      id={getActEventExpertsBlockId(actEventId)}
      button={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            variant="link"
            color="primary"
            type="button"
            disabled={isWatchOnly}
            onClick={addExpert}
          />
        </>
      }
    >
      <FieldArray name={name} validate={validateExpertsArray}>
        {({ fields, meta }: FieldArrayRenderProps<IKnmActExpertSelectOption, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          const arrayErrorMessage = getExpertsArrayErrorMessage(fields.value, meta.error);
          const shouldShowArrayError = Boolean(arrayErrorMessage) && (meta.touched || meta.submitFailed || meta.dirty);

          return (
            <>
              {shouldShowArrayError && (
                <p className={`shared-input-error shared-row__small-left-margin ${styles.ErrorMsgWrapper}`}>
                  {arrayErrorMessage}
                </p>
              )}
              <Row>
                {fields.map((expertsFieldName, sIndex) => {
                  const fieldValue: IKnmActExpertSelectOption | undefined = fields.value?.[sIndex];
                  const expertBlockId = fieldValue?.id || String(sIndex);
                  const id = getActEventExpertBlockId(actEventId, expertBlockId);

                  return (
                    <Row key={id} isListItem notSpace>
                      <SelectInputField
                        className={styles.SelectInputExperts}
                        name={expertsFieldName}
                        id={id}
                        placeholder={getTitleFieldWithRedStar({
                          label: 'Выберите эксперта/экспертную организацию, участвующую в осуществлении действия',
                          field: expertsFieldName,
                          isKnm: true,
                        })}
                        options={expertsErknmOptionsFiltered}
                        isSearchable
                        disabled={isWatchOnly}
                        size="medium"
                      />
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveExpertHandler(sIndex)} />
                    </Row>
                  );
                })}
              </Row>
            </>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};

export const ErknmActItemEventExperts = connect(null)(ErknmActItemEventExpertsWithoutStore);
